// src/pages/Compare.js
import React from "react";
import { NavLink, useParams, Link } from "react-router-dom";
import bossdropLogo from "../images/otherlogowhite-380.webp";
import motionLogo from "../images/Use Motion's_idlawkthCl_1.svg";
import clickupLogo from "../images/logo-v3-clickup-dark.svg";
import mondayLogo from "../images/monday-400.webp";
import wrikelogo from "../images/wrike-wordmark-1.svg";
import viktorLogo from "../images/viktor-wordmark.svg";
import escalationChain480 from "../images/escalation-chain-2026-480.webp";
import escalationChain960 from "../images/escalation-chain-2026-960.webp";
import escalationChain1260 from "../images/escalation-chain-2026-1260.webp";
import escalationChainMobileImg from "../images/escalation-chain-2026-mobile-720.webp";
import escalationClickup480 from "../images/escalation-chain-2026-clickup-480.webp";
import escalationClickup960 from "../images/escalation-chain-2026-clickup-960.webp";
import escalationClickup1260 from "../images/escalation-chain-2026-clickup-1260.webp";
import escalationClickupMobileImg from "../images/escalation-chain-2026-clickup-mobile-720.webp";
import escalationMonday480 from "../images/escalation-chain-2026-monday-480.webp";
import escalationMonday960 from "../images/escalation-chain-2026-monday-960.webp";
import escalationMonday1260 from "../images/escalation-chain-2026-monday-1260.webp";
import escalationMondayMobileImg from "../images/escalation-chain-2026-monday-mobile-720.webp";
import SEO from "../components/SEO";
import { DEMO_LINK } from "../components/demoBooking";

/** ------- Brand assets ------- */
const LOGOS = {
  bossdrop: bossdropLogo,
  motion: motionLogo,
  clickup: clickupLogo,
  monday: mondayLogo,
  wrike: wrikelogo,
  viktor: viktorLogo,
};

/** Hero product shot: the same escalation story everywhere. The ClickUp and
 *  monday.com pages show the stuck task in that tool; the other pages show
 *  it in Asana, like the homepage. */
const SHOTS = {
  default: {
    img: escalationChain960,
    srcSet: `${escalationChain480} 480w, ${escalationChain960} 960w, ${escalationChain1260} 1260w`,
    mobileImg: escalationChainMobileImg,
    task: "a stuck Asana task",
  },
  clickup: {
    img: escalationClickup960,
    srcSet: `${escalationClickup480} 480w, ${escalationClickup960} 960w, ${escalationClickup1260} 1260w`,
    mobileImg: escalationClickupMobileImg,
    task: "a stuck ClickUp task",
  },
  monday: {
    img: escalationMonday960,
    srcSet: `${escalationMonday480} 480w, ${escalationMonday960} 960w, ${escalationMonday1260} 1260w`,
    mobileImg: escalationMondayMobileImg,
    task: "a stuck monday.com item",
  },
};

/** ------- Data (brand color, tagline, table rows) -------
 *
 * Two kinds of page:
 *  - kind "compare": a tool BossDrop is an alternative to (Viktor, Motion,
 *    Wrike). Columns are BossDrop vs the other tool.
 *  - kind "integration": a tool BossDrop connects to and runs on top of
 *    (ClickUp, monday.com). Columns are "with BossDrop" vs "the tool alone",
 *    and the page never pitches BossDrop as a replacement.
 *
 * BossDrop rows may only claim what the product does today: read work across
 * the 14 connected tools, chase status, nudge owners, escalate to the owner's
 * manager, draft and update tasks (approval-gated), schedule decision
 * meetings, send the Friday Weekly Executive Summary, log every action in
 * Audit Logs. No boards, roadmaps, capacity planning, time tracking or docs.
 *
 * Symbols: "✓" built in, "~" partly, "no" not included. Text values render
 * as text.
 */
const COMPETITORS = {
  // Viktor rows checked against viktor.com product, docs and pricing pages on
  // 2026-10-07. Rows we could not verify for Viktor (approvals, audit log,
  // roster limits) are left out rather than guessed.
  viktor: {
    slug: "viktor",
    kind: "compare",
    name: "Viktor",
    color: "#e5e7eb",
    logo: LOGOS.viktor,
    tagline:
      "Viktor is a general-purpose AI coworker you ask to do work across thousands of apps. BossDrop is built for one job: running your team's follow-through. It chases status, escalates stuck work up the reporting line and reports to leadership on a schedule, with approvals where they matter.",
    seoDescription:
      "Viktor is a general AI coworker. BossDrop is built for follow-through: it chases status, escalates stuck work up the reporting line and reports to leadership on a schedule.",
    pros: [
      "Escalates stuck work up the reporting line, one level at a time",
      "Leadership report on a schedule: risks, decisions needed, owners",
      "Approvals, roster-only messaging and a full audit log",
    ],
    considerIf: [
      "You want a general AI assistant for many kinds of tasks",
      "You need connections to thousands of apps",
      "You're happy to set up each workflow yourself in chat",
    ],
    rows: [
      ["Works inside Slack or Microsoft Teams", "✓", "✓"],
      ["Connects to your task tool (Jira, Asana, Linear, ClickUp, Notion, monday.com, Planner)", "✓", "✓"],
      ["Nudges owners when work slips", "✓", "✓"],
      ["Built-in escalation up the reporting line", "✓", " - "],
      ["Scheduled leadership report with risks, decisions and owners", "✓", "~"],
      ["Risk register built from task data", "✓", "~"],
      ["General tasks on request (reports, CRM updates, code)", "~", "✓"],
      ["App integrations", "14 core tools", "3,200+"],
      ["Pricing", "Per team", "Usage credits"],
    ],
  },
  // Motion column checked against usemotion.com/pricing on 2026-10-18
  // (auto-scheduling calendar, AI Gantt chart, team capacity planning, priced
  // per seat). Follow-up and escalation features are not listed there.
  motion: {
    slug: "motion",
    kind: "compare",
    name: "Motion",
    color: "#000000",
    logo: LOGOS.motion,
    // Black wordmark: invert it so it reads on the dark theme.
    invertLogo: true,
    tagline:
      "Motion is an AI planner: it auto-schedules tasks onto calendars and keeps projects in its own workspace. BossDrop is not another planner. It runs inside the tools your team already uses and does the coordination work around them: chasing status, escalating stuck work to the owner's manager and sending a Friday executive summary.",
    seoDescription:
      "Motion auto-schedules tasks onto calendars. BossDrop runs inside the tools your team already uses and handles follow-through: status chasing, escalations and a Friday executive summary.",
    pros: [
      "Works inside the tools you already use, nothing to migrate",
      "Escalates stuck work to the owner's manager",
      "Friday executive summary with risks and decisions needed",
      "Approvals where they matter, and every action in an audit log",
    ],
    considerIf: [
      "You want AI to plan and time-block each person's day",
      "You're ready to move tasks and projects into a new tool",
      "Your bigger problem is planning the work, not following through on it",
    ],
    rows: [
      ["Where your tasks live", "Your existing tools", "In Motion"],
      ["Auto-schedules tasks onto each person's calendar", "no", "✓"],
      ["Projects, Gantt charts and capacity planning", "no", "✓"],
      ["Chases owners for status updates", "✓", "~"],
      ["Escalates stuck work to the owner's manager", "✓", "no"],
      ["Friday executive summary across all your tools", "✓", "~"],
      ["Pricing", "Per team", "Per seat"],
    ],
    sourceNote:
      "Motion column based on Motion's public pricing and feature page, October 2026.",
  },
  // Wrike column checked against wrike.com/price on 2026-10-18 (Gantt charts,
  // resource and capacity planning, time tracking on upper plans, priced per
  // user). Follow-up and escalation features are not listed there.
  wrike: {
    slug: "wrike",
    kind: "compare",
    name: "Wrike",
    color: "#10B981",
    logo: LOGOS.wrike,
    // Near-black wordmark: flip its lightness so it reads on the dark theme.
    invertLogo: true,
    tagline:
      "Wrike is a full work management platform, with plans, Gantt charts and resource planning inside Wrike. BossDrop doesn't replace a PM platform. It runs inside the tools your teams already use and handles the follow-through: chasing status, escalating stuck work to the owner's manager and sending a Friday executive summary.",
    seoDescription:
      "Wrike is a work management platform. BossDrop runs inside the tools your teams already use and handles follow-through: status chasing, escalations and a Friday executive summary.",
    pros: [
      "Works across the tools your teams already use, nothing to migrate",
      "Escalates stuck work to the owner's manager",
      "Friday executive summary with risks and decisions needed",
      "Approvals where they matter, and every action in an audit log",
    ],
    considerIf: [
      "You want one platform to plan and run all project work",
      "You need Gantt charts, resource planning and time tracking",
      "You have admins to set up workflows, templates and permissions",
    ],
    rows: [
      ["Where your tasks live", "Your existing tools", "In Wrike"],
      ["Gantt charts and resource planning", "no", "✓"],
      ["Time tracking", "no", "✓"],
      ["Chases owners for status updates", "✓", "~"],
      ["Escalates stuck work to the owner's manager", "✓", "no"],
      ["Friday executive summary across all your tools", "✓", "~"],
      ["Pricing", "Per team", "Per user"],
    ],
    sourceNote:
      "Wrike column based on Wrike's public pricing and plans page, October 2026.",
  },
  // ClickUp and monday.com are integrations (OAuth connectors, Oct 2026):
  // BossDrop reads and writes tasks there, so these pages are "with" pages.
  clickup: {
    slug: "clickup",
    kind: "integration",
    name: "ClickUp",
    color: "#7C3AED",
    logo: LOGOS.clickup,
    tagline:
      "Keep ClickUp. BossDrop connects to ClickUp with OAuth and does the coordination work around your tasks: it chases owners for status updates, nudges them when work slips, escalates stuck work to the owner's manager and sends a Friday executive summary. Every action lands in an audit log.",
    seoDescription:
      "Keep ClickUp. BossDrop connects through OAuth, chases task owners for updates, escalates stuck work to their manager and sends a Friday executive summary.",
    pros: [
      "Chases owners for updates in Slack, Teams or email",
      "Escalates stuck tasks to the owner's manager",
      "Friday summary across ClickUp and your other tools",
      "Drafts and updates ClickUp tasks, with approvals where they matter",
    ],
    keeps: [
      "Your spaces, lists and tasks, exactly as they are",
      "Your views, statuses and automations",
      "Your team plans and does the work in ClickUp, same as today",
      "Nothing to migrate or rebuild",
    ],
    rows: [
      ["Your spaces, lists, tasks and views", "✓", "✓"],
      ["Chases owners for status updates", "✓", "~"],
      ["Escalates stuck work to the owner's manager", "✓", "no"],
      ["Friday executive summary across all your tools", "✓", "~"],
      ["Works across Slack, Teams and email, not just inside ClickUp", "✓", "~"],
      ["Every automated action in an audit log", "✓", "~"],
    ],
  },
  monday: {
    slug: "monday",
    kind: "integration",
    name: "monday.com",
    color: "#22C55E",
    logo: LOGOS.monday,
    tagline:
      "Keep monday.com. BossDrop connects to monday.com with OAuth and does the coordination work around your boards: it chases item owners for updates, nudges them when work slips, escalates stuck items to the owner's manager and sends a Friday executive summary. Every action lands in an audit log.",
    seoDescription:
      "Keep monday.com. BossDrop connects through OAuth, chases item owners for updates, escalates stuck work to their manager and sends a Friday executive summary.",
    pros: [
      "Chases item owners for updates in Slack, Teams or email",
      "Escalates stuck items to the owner's manager",
      "Friday summary across monday.com and your other tools",
      "Drafts and updates items, with approvals where they matter",
    ],
    keeps: [
      "Your boards, groups and items, exactly as they are",
      "Your columns, views and automations",
      "Your team plans and does the work in monday.com, same as today",
      "Nothing to migrate or rebuild",
    ],
    rows: [
      ["Your boards, items and views", "✓", "✓"],
      ["Chases item owners for status updates", "✓", "~"],
      ["Escalates stuck work to the owner's manager", "✓", "no"],
      ["Friday executive summary across all your tools", "✓", "~"],
      ["Works across Slack, Teams and email, not just inside the board", "✓", "~"],
      ["Every automated action in an audit log", "✓", "~"],
    ],
  },
};

const isIntegration = (c) => c.kind === "integration";
const COMPARE_LIST = Object.values(COMPETITORS).filter((c) => !isIntegration(c));
const INTEGRATION_LIST = Object.values(COMPETITORS).filter(isIntegration);
const pageTitle = (c) =>
  isIntegration(c) ? `BossDrop + ${c.name}` : `BossDrop vs ${c.name}`;
const switcherLabel = (c) =>
  isIntegration(c) ? `with ${c.name}` : `vs ${c.name}`;

// Helper to style ✓ / ~ / no
function renderSymbol(val) {
  const raw = String(val ?? "").trim();
  const lower = raw.toLowerCase();

  const isOk = raw === "✓" || ["check", "yes", "true"].includes(lower);
  const isPartial = raw === "~" || ["partial", "maybe"].includes(lower);
  const isNo =
    raw === " - " ||
    raw === "-" ||
    ["x", "✕", "×", "no", "false"].includes(lower);

  if (isOk)
    return (
      <span className="sym sym-ok" role="img" aria-label="Included" title="Included" />
    );
  if (isPartial)
    return (
      <span className="sym sym-partial" role="img" aria-label="Partly" title="Partly" />
    );
  if (isNo)
    return (
      <span className="sym sym-no" role="img" aria-label="Not included" title="Not included" />
    );

  // fall back to the original text (e.g., "Per team", "In Motion")
  return raw;
}

// Small layout tweaks for this page only. Kept here so the page's styles stay
// with the page; class names are prefixed cmpx- to avoid collisions.
const PAGE_CSS = `
.cmpx-legend { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; margin: 0 0 12px; }
.cmpx-legend-item { display: inline-flex; align-items: center; gap: 8px; }
.cmpx-legend .sym { --size: 20px; box-shadow: none; }
.cmpx-legend .sym::before { display: none; }
.cmpx-th-sub { display: block; margin-top: 4px; font-size: 12px; font-weight: 600; letter-spacing: .02em; opacity: .75; }
.cmpx-text-cell { font-size: 14px; font-weight: 600; }
.cmpx-section-head { margin: 32px 0 4px; }
.cmpx-section-head + .muted { margin: 0; }
.cmpx-shot picture, .cmpx-shot img { display: block; width: 100%; }
.cmpx-note { margin-top: 10px; font-size: 13px; }
.cmp-hero-copy .kpis.cmp-chips { justify-content: flex-start; }
.cmp-card .cmp-card-head { gap: 8px; }
.cmp-card .brandBadge-img { height: 18px; max-height: 18px; width: auto; }
/* Flip lightness but keep hue, so dark wordmarks read on the dark theme. */
.cmpx-invert { filter: invert(1) hue-rotate(180deg); }
@media (max-width: 600px) {
  .cmp-table { min-width: 0; table-layout: fixed; }
  .cmp-table th:first-child, .cmp-table td:first-child { width: 44%; }
  .cmp-table thead th:not(:first-child), .cmp-table td:not(:first-child) { width: 28%; }
  .cmp-table .cmp-th-logo { width: auto; height: auto; max-width: 100%; max-height: 18px; }
  .cmp-table td, .cmp-table th { overflow-wrap: anywhere; }
  .cmpx-text-cell { font-size: 13px; }
  .cmpx-th-sub { font-size: 11px; }
}
`;

/** Router wrapper for /compare and /compare/:slug */
export default function CompareRouter() {
  const { slug } = useParams();
  if (!slug) return <CompareIndex />;
  const data = COMPETITORS[slug];
  if (!data) return <CompareIndex unknownSlug={slug} />;
  return <ComparePage data={data} />;
}

/** Landing: /compare */
function CompareIndex({ unknownSlug }) {
  return (
    <section className="page container">
      <style>{PAGE_CSS}</style>
      <SEO
        title="BossDrop | Compare"
        description="BossDrop is a coordination layer inside the tools you already use. Compare it with Viktor, Motion and Wrike, and see how it works with ClickUp and monday.com."
        canonical="https://bossdrop.ai/compare"
        url="https://bossdrop.ai/compare"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "BossDrop",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bossdrop.ai/",
          publisher: {
            "@type": "Organization",
            name: "BossDrop",
            url: "https://bossdrop.ai/",
          },
        }}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bossdrop.ai/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Compare",
              item: "https://bossdrop.ai/compare",
            },
          ],
        }}
      />
      <div className="hero cmp-hero reveal" data-instant>
        <span className="pill">Compare</span>
        <h1 className="h1">How BossDrop compares</h1>
        <p className="sub">
          BossDrop is a coordination layer, not another task tool. It runs
          inside Slack, Teams, email and the task tools you already use, then
          chases status, escalates stuck work to the owner's manager and sends
          a Friday executive summary. See how it compares with Viktor, Motion
          and Wrike, and how it works with ClickUp and monday.com.
        </p>
        {!!unknownSlug && (
          <p className="muted" style={{ marginTop: 6 }}>
            We couldn’t find <code>{unknownSlug}</code>. Pick one below.
          </p>
        )}
      </div>

      <h2 className="h2 cmpx-section-head">Compared with other tools</h2>
      <p className="muted">
        Different tools for a different job. Here is where each one fits.
      </p>
      <div className="grid cards">
        {COMPARE_LIST.map((c, i) => (
          <CompareCard key={c.slug} c={c} delay={100 + i * 60} />
        ))}
      </div>

      <h2 className="h2 cmpx-section-head">Works with your task tool</h2>
      <p className="muted">
        ClickUp and monday.com are integrations. Keep the tool; BossDrop does
        the coordination work around it.
      </p>
      <div className="grid cards">
        {INTEGRATION_LIST.map((c, i) => (
          <CompareCard key={c.slug} c={c} delay={100 + i * 60} />
        ))}
      </div>
    </section>
  );
}

function CompareCard({ c, delay }) {
  const together = isIntegration(c);
  return (
    <article className="card cmp-card reveal" data-delay={`${delay}ms`}>
      <div className="cmp-card-head">
        <BrandBadge name="BossDrop" logo={LOGOS.bossdrop} />
        <span className="cmp-vs" aria-hidden="true">
          {together ? "+" : "vs"}
        </span>
        <BrandBadge name={c.name} logo={c.logo} color={c.color} invert={c.invertLogo} />
      </div>
      <h3 style={{ marginTop: 8 }}>{pageTitle(c)}</h3>
      <p className="muted" style={{ margin: "6px 0 12px" }}>
        {c.tagline}
      </p>
      <div className="ctaRow">
        <NavLink className="btn btnPrimary" to={`/compare/${c.slug}`}>
          {together ? "See how it works" : "Read comparison"}
        </NavLink>
        <a className="btn btnCream" {...DEMO_LINK}>
          Book a demo
        </a>
      </div>
    </article>
  );
}

/** Single page: /compare/:slug */
function ComparePage({ data }) {
  const together = isIntegration(data);
  const title = pageTitle(data);
  const url = `https://bossdrop.ai/compare/${data.slug}`;
  const shot = SHOTS[data.slug] || SHOTS.default;

  return (
    <section className="page container">
      <style>{PAGE_CSS}</style>
      <SEO
        title={`${title} | Compare`}
        description={data.seoDescription}
        canonical={url}
        url={url}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bossdrop.ai/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Compare",
              item: "https://bossdrop.ai/compare",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: title,
              item: url,
            },
          ],
        }}
      />
      {/* HERO SPLIT: logos + headline + screenshot */}
      <div className="cmp-split reveal" data-instant>
        <div className="cmp-hero-copy">
          <div className="cmp-logos">
            <BrandBadge
              name="BossDrop"
              logo={LOGOS.bossdrop}
              color={"#b7a4e6"}
            />
            <span className="cmp-vs-dot" aria-hidden="true">
              {together ? "+" : "vs"}
            </span>
            <BrandBadge
              name={data.name}
              logo={data.logo}
              color={data.color}
              invert={data.invertLogo}
            />
          </div>

          <h1 className="h1" style={{ marginTop: 10 }}>
            {title}
          </h1>
          <p className="sub">{data.tagline}</p>

          <div className="ctaRow">
            <a className="btn btnPrimary" {...DEMO_LINK}>
              Book a demo
            </a>
            <Link className="btn btnGhost" to="/features">
              Explore features
            </Link>
          </div>

          {/* quick highlight chips */}
          <div className="kpis cmp-chips">
            {data.pros.slice(0, 3).map((p) => (
              <span key={p} className="pill">
                {p}
              </span>
            ))}
          </div>
        </div>

        {/* Product shot: the owner is chased first, then their manager. */}
        <figure className="screenshot-frame cmpx-shot reveal">
          <picture>
            <source
              media="(max-width: 600px)"
              srcSet={shot.mobileImg}
              width="720"
              height="1372"
            />
            <img
              src={shot.img}
              srcSet={shot.srcSet}
              sizes="(max-width: 980px) min(100vw - 60px, 620px), 460px"
              width="1260"
              height="788"
              alt={`BossDrop follow-through on ${shot.task}: it asks the owner in Microsoft Teams and follows up once, then emails an escalation to the owner's manager, approved to send and logged in Audit Logs.`}
              loading="lazy"
              decoding="async"
            />
          </picture>
          <figcaption className="muted">
            Owner asked first, manager looped in, every step logged.
          </figcaption>
        </figure>
      </div>

      {/* HIGHLIGHTS */}
      <div className="grid pricing">
        <div className="card cmp-anim-card reveal" data-delay="80ms">
          <h3>
            {together
              ? `What BossDrop adds to ${data.name}`
              : "Why teams pick BossDrop"}
          </h3>
          <ul className="bullets">
            {data.pros.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
        <div className="card cmp-anim-card reveal" data-delay="140ms">
          <h3>
            {together
              ? `What stays in ${data.name}`
              : `When ${data.name} might fit`}
          </h3>
          <ul className="bullets">
            {(together ? data.keeps : data.considerIf).map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* COMPARISON TABLE */}
      <div className="panel reveal" data-delay="200ms">
        <h2 className="h2">
          {together
            ? `${data.name} with BossDrop vs alone`
            : "Feature comparison"}
        </h2>
        <p className="muted" style={{ margin: "0 0 10px" }}>
          {together
            ? `What ${data.name} does on its own, and what changes when BossDrop runs on top of it.`
            : "A high-level view of where each tool fits."}
        </p>
        <div className="cmpx-legend muted" aria-hidden="true">
          <span className="cmpx-legend-item">
            <span className="sym sym-ok" /> Built in
          </span>
          <span className="cmpx-legend-item">
            <span className="sym sym-partial" /> Partly
          </span>
          <span className="cmpx-legend-item">
            <span className="sym sym-no" /> Not included
          </span>
        </div>

        <div className="cmp-table-wrap">
          <table className="cmp-table">
            <thead>
              <tr>
                <th>Capability</th>

                {/* Left column: BossDrop (or the tool with BossDrop on top) */}
                <th className="cmp-th-brand">
                  <span className="sr-only">
                    {together ? `${data.name} with BossDrop` : "BossDrop"}
                  </span>
                  <img src={LOGOS.bossdrop} alt="" className="cmp-th-logo" />
                  {together && (
                    <span className="cmpx-th-sub" aria-hidden="true">
                      with {data.name}
                    </span>
                  )}
                </th>

                {/* Right column: the other tool (alone, for integrations) */}
                <th className="cmp-th-brand">
                  <span className="sr-only">
                    {together ? `${data.name} alone` : data.name}
                  </span>
                  <img
                    src={data.logo}
                    alt=""
                    className={`cmp-th-logo${data.invertLogo ? " cmpx-invert" : ""}`}
                  />
                  {together && (
                    <span className="cmpx-th-sub" aria-hidden="true">
                      alone
                    </span>
                  )}
                </th>
              </tr>
            </thead>

            <tbody>
              {data.rows.map(([cap, ours, theirs], i) => (
                <tr key={cap + i}>
                  <td>{cap}</td>
                  <td className="cmp-cell-strong">
                    <Cell value={ours} />
                  </td>
                  <td>
                    <Cell value={theirs} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.sourceNote && (
          <p className="muted cmpx-note">{data.sourceNote}</p>
        )}

        <div className="ctaRow" style={{ marginTop: 16 }}>
          <a className="btn btnPrimary" {...DEMO_LINK}>
            See it live
          </a>
          <Link className="btn btnGhost" to="/pricing">
            View pricing
          </Link>
        </div>
      </div>

      {/* PILL SWITCHER */}
      <div className="cmp-switcher reveal" data-delay="240ms">
        {[...COMPARE_LIST, ...INTEGRATION_LIST].map((c) => (
          <NavLink key={c.slug} to={`/compare/${c.slug}`} className="pill">
            {switcherLabel(c)}
          </NavLink>
        ))}
      </div>
    </section>
  );
}

/** ------- Small presentational bits ------- */
function Cell({ value }) {
  const out = renderSymbol(value);
  return typeof out === "string" ? (
    <span className="cmpx-text-cell">{out}</span>
  ) : (
    out
  );
}

function BrandBadge({ name, logo, color, invert }) {
  return (
    <span
      className="brandBadge"
      style={
        color
          ? {
              // tint competitor badges slightly
              boxShadow: `0 0 0 1px color-mix(in oklab, ${color} 55%, #fff 8%) inset`,
              background:
                "linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.03))",
            }
          : undefined
      }
      title={name}
    >
      <img
        src={logo}
        alt=""
        className={`brandBadge-img${invert ? " cmpx-invert" : ""}`}
      />
    </span>
  );
}
